export type ExportBackground = "transparent" | "white" | "custom";
export type ExportFormat = "png" | "jpeg" | "webp";
export type ExportSize = "original" | 1000 | 2000 | 2048 | "custom";
export type ExportPresetId = "transparent" | "white" | "amazon" | "etsy" | "shopify" | "ebay" | "custom";

export interface ExportSettings {
  background: ExportBackground;
  customColor: string;
  size: ExportSize;
  customSize: number;
  fitSquare: boolean;
  format: ExportFormat;
  quality: number;
}

export interface ExportPreset {
  id: ExportPresetId;
  label: string;
  description: string;
  settings: ExportSettings;
}

const base: ExportSettings = {
  background: "transparent",
  customColor: "#ffffff",
  size: "original",
  customSize: 1600,
  fitSquare: false,
  format: "png",
  quality: 0.92,
};

export const EXPORT_PRESETS: ExportPreset[] = [
  { id: "transparent", label: "Transparent PNG", description: "Original size, transparent PNG", settings: { ...base } },
  { id: "white", label: "White background", description: "Original size, white PNG", settings: { ...base, background: "white" } },
  { id: "amazon", label: "Amazon", description: "2000 x 2000, white JPEG", settings: { ...base, background: "white", size: 2000, fitSquare: true, format: "jpeg" } },
  { id: "etsy", label: "Etsy", description: "2000 x 2000, white JPEG", settings: { ...base, background: "white", size: 2000, fitSquare: true, format: "jpeg" } },
  { id: "shopify", label: "Shopify", description: "2048 x 2048, white JPEG", settings: { ...base, background: "white", size: 2048, fitSquare: true, format: "jpeg" } },
  { id: "ebay", label: "eBay", description: "1600 x 1600, white JPEG", settings: { ...base, background: "white", size: "custom", customSize: 1600, fitSquare: true, format: "jpeg" } },
  { id: "custom", label: "Custom", description: "Your export settings", settings: { ...base } },
];

export function presetById(id: ExportPresetId): ExportPreset {
  return EXPORT_PRESETS.find((preset) => preset.id === id) ?? EXPORT_PRESETS[0];
}

export function safeBaseName(filename: string): string {
  return filename.replace(/\.[^.]+$/, "").normalize("NFKD").replace(/[^a-z0-9-_]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase() || "image";
}

export function exportExtension(format: ExportFormat): "png" | "jpg" | "webp" {
  return format === "jpeg" ? "jpg" : format;
}

export function exportFilename(filename: string, settings: ExportSettings): string {
  const background = settings.background === "transparent" ? "transparent" : settings.background === "white" ? "white" : settings.customColor.slice(1).toLowerCase();
  const size = settings.size === "original" ? "original" : settings.size === "custom" ? Math.round(settings.customSize) : settings.size;
  return `${safeBaseName(filename)}-${background}-${size}.${exportExtension(settings.format)}`;
}

export function exportDimensions(width: number, height: number, settings: ExportSettings): [number, number] {
  const requested = settings.size === "original" ? null : settings.size === "custom" ? settings.customSize : settings.size;
  if (settings.fitSquare) {
    const side = Math.max(1, Math.min(4096, Math.round(requested ?? Math.max(width, height))));
    return [side, side];
  }
  if (!requested) return [width, height];
  const scale = Math.min(1, Math.min(4096, Math.max(1, requested)) / Math.max(width, height));
  return [Math.max(1, Math.round(width * scale)), Math.max(1, Math.round(height * scale))];
}

function loadBlobImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => { URL.revokeObjectURL(url); resolve(image); };
    image.onerror = () => { URL.revokeObjectURL(url); reject(new Error("The result could not be prepared for export.")); };
    image.src = url;
  });
}

function subjectBounds(image: HTMLImageElement): { x: number; y: number; width: number; height: number } {
  const scale = Math.min(1, 1024 / Math.max(image.naturalWidth, image.naturalHeight));
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) return { x: 0, y: 0, width: image.naturalWidth, height: image.naturalHeight };
  context.drawImage(image, 0, 0, width, height);
  const pixels = context.getImageData(0, 0, width, height).data;
  let left = width;
  let right = -1;
  let top = height;
  let bottom = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (pixels[(y * width + x) * 4 + 3] < 8) continue;
      left = Math.min(left, x);
      right = Math.max(right, x);
      top = Math.min(top, y);
      bottom = Math.max(bottom, y);
    }
  }
  if (right < left || bottom < top) return { x: 0, y: 0, width: image.naturalWidth, height: image.naturalHeight };
  return {
    x: left / scale,
    y: top / scale,
    width: (right - left + 1) / scale,
    height: (bottom - top + 1) / scale,
  };
}

export async function renderExportBlob(cutout: Blob, requested: ExportSettings): Promise<Blob> {
  const image = await loadBlobImage(cutout);
  const settings = requested.format === "png" ? requested : { ...requested, background: requested.background === "transparent" ? "white" as const : requested.background };
  const [width, height] = exportDimensions(image.naturalWidth, image.naturalHeight, settings);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Your browser could not create the export.");
  if (settings.background !== "transparent") {
    context.fillStyle = settings.background === "white" ? "#ffffff" : settings.customColor;
    context.fillRect(0, 0, width, height);
  }
  if (settings.fitSquare) {
    const bounds = subjectBounds(image);
    const available = Math.min(width, height) * 0.9;
    const fit = Math.min(available / bounds.width, available / bounds.height);
    const drawWidth = bounds.width * fit;
    const drawHeight = bounds.height * fit;
    context.drawImage(image, bounds.x, bounds.y, bounds.width, bounds.height, (width - drawWidth) / 2, (height - drawHeight) / 2, drawWidth, drawHeight);
  } else {
    context.drawImage(image, 0, 0, width, height);
  }
  const mime = `image/${settings.format}`;
  return new Promise((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("The export could not be encoded.")), mime, settings.quality));
}
